import React from 'react';
import { ArrowLeft, Github, Database, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

// Import custom images
import resortHome from "@/assets/projects/resort/resort_home_page.png"; 
import adminPanel from "@/assets/projects/resort/admin_panel.png"; 
import adminAccess from "@/assets/projects/resort/admin_acess.png"; 

const MSCastleBookingApp: React.FC = () => {
  return (
    <main className="min-h-screen bg-background">
      {/* Back Navigation */}
      <div className="max-w-6xl mx-auto px-4 pt-16 md:pt-20">
        <Link to="/" className="flex items-center text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Portfolio
        </Link>
      </div>
      
      {/* Hero Section */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col gap-4">
            <Badge variant="secondary" className="w-fit">Full Stack Web Project</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">MS Castle Resort Booking Platform</h1>
            <div className="flex flex-wrap gap-2 my-4">
              <Badge variant="outline" className="bg-white/5 border-primary/20">ReactJS</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Vite</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Firebase Auth</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Firestore DB</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">WhatsApp Notifications</Badge>
            </div>
          </div>
        </div>
      </section>
      
      {/* Project Image Panel */}
      <section className="py-6 bg-muted/5">
        <div className="max-w-6xl mx-auto px-4">
          <div className="w-full overflow-hidden rounded-xl border border-border shadow-sm bg-black">
            <Carousel
              showThumbs={false}
              showStatus={false}
              showIndicators={true}
              infiniteLoop
              autoPlay
              interval={5000}
              className="project-carousel rounded-xl overflow-hidden bg-black"
            >
              <div className="aspect-video flex items-center justify-center w-full h-full bg-black">
                <img src={resortHome} alt="Resort Home Page" className="w-full h-full object-contain" />
              </div>
              <div className="aspect-video flex items-center justify-center w-full h-full bg-black">
                <img src={adminPanel} alt="Admin Panel" className="w-full h-full object-contain" />
              </div>
              <div className="aspect-video flex items-center justify-center w-full h-full bg-black">
                <img src={adminAccess} alt="Admin Access Login" className="w-full h-full object-contain" />
              </div>
            </Carousel>
          </div>
        </div>
      </section>
      
      {/* Project Details */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="md:col-span-2 space-y-6">
              <div className="bg-card p-8 rounded-xl border border-border shadow-sm">
                <h2 className="text-2xl font-bold mb-4 text-foreground">Project Overview</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Built specifically for resort booking, this application draws inspiration directly from platforms like Airbnb to deliver a premium user experience. It provides guests with immersive property galleries and a powerful reservation system that manages real-time booking slots. Whenever a guest submits a reservation, the system automatically triggers an instant booking notification via WhatsApp, ensuring immediate communication between the property and the client.
                </p>
                <h3 className="text-xl font-semibold mb-3 text-foreground">How It Works</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Guests navigate a beautifully crafted React UI to explore the property and select their desired dates from a live-updating interactive calendar. Upon submission, the resort administration receives the request in a secure, dedicated Admin Panel. This secure admin space is fortified by Firebase Authentication via Google login. Once logged in, the admin can approve or modify dates, seamlessly persisting changes to a high-performance Cloud Firestore database that instantly syncs the new availability to all online users.
                </p>
                <h3 className="text-xl font-semibold mb-3 text-foreground">Key Highlights</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-6">
                  <li><strong>Airbnb-Style Interface:</strong> Intuitive layout featuring rich media galleries and guest reviews.</li>
                  <li><strong>Realtime Booking Slots:</strong> Live synchronization ensuring no double-bookings ever occur.</li>
                  <li><strong>WhatsApp Integration:</strong> Instant booking requests and notifications pushed to WhatsApp for rapid response times.</li>
                  <li><strong>Protected Admin Panel:</strong> A dedicated control center for authorized staff to review, approve, and manage calendar slots.</li>
                  <li><strong>Firebase Auth & Firestore:</strong> Enterprise-grade security for authentication, paired with a blazing-fast NoSQL database for managing property data securely.</li>
                </ul>
              </div>
            </div>
            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Project Links</h3>
                <div className="space-y-3">
                  <Button variant="outline" size="lg" className="w-full justify-start hover:bg-primary/10 transition-colors" asChild>
                    <a href="https://mscastlekodaikanal.web.app/" target="_blank" rel="noopener noreferrer" className="flex items-center">
                      <Globe className="mr-2 h-5 w-5" />
                      <span>Visit Live Site</span>
                    </a>
                  </Button>
                  <Button variant="outline" size="lg" className="w-full justify-start opacity-75 cursor-not-allowed" asChild disabled>
                    <div className="flex items-center">
                      <Github className="mr-2 h-5 w-5" />
                      <span>Source Code (Private / Client)</span>
                    </div>
                  </Button>
                </div>
              </div>
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Technologies Used</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">Re</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">React & Vite</p>
                      <p className="text-sm text-muted-foreground">Frontend Framework</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">FB</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Firebase Auth</p>
                      <p className="text-sm text-muted-foreground">Secure Login</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">FS</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Firestore</p>
                      <p className="text-sm text-muted-foreground">Database</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">WA</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">WhatsApp API</p>
                      <p className="text-sm text-muted-foreground">Notifications</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default MSCastleBookingApp;
